import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Shield, Edit, Eye, UserPlus, Key, Lock, Unlock, User, Calendar } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { AppSidebar } from '@/components/AppSidebar';
import { SidebarProvider } from '@/components/ui/sidebar';

interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  company: string | null;
  position: string | null;
  avatar_url: string | null;
  created_at: string;
  is_blocked: boolean;
  blocked_at: string | null;
  blocked_by: string | null;
  user_roles: Array<{ role: string }>;
}

const UserManagement = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [blockAlertOpen, setBlockAlertOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [processingAction, setProcessingAction] = useState(false);
  const [changingRoleUserId, setChangingRoleUserId] = useState<string | null>(null);
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    full_name: '',
    company: '',
    role: 'guest' as 'admin' | 'editor' | 'cliente' | 'guest' | 'rsvp'
  });
  const [creating, setCreating] = useState(false);
  const { role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!roleLoading && role !== 'admin') {
      toast({
        title: 'Acesso negado',
        description: 'Apenas administradores podem acessar esta página.',
        variant: 'destructive',
      });
      navigate('/');
    }
  }, [role, roleLoading, navigate, toast]);

  useEffect(() => {
    if (role === 'admin') {
      fetchUsers();
    }
  }, [role]);

  const fetchUsers = async () => {
    try {
      console.log('📊 Fetching users via Edge Function...');
      
      // Call the list-users Edge Function
      const { data, error } = await supabase.functions.invoke('list-users', {
        method: 'GET'
      });

      if (error) {
        console.error('Error from Edge Function:', error);
        throw error;
      }

      if (!data?.users) {
        throw new Error('No users data returned from Edge Function');
      }

      console.log('✅ Users loaded successfully:', data.users.length);
      console.log('📋 User roles details:', data.users.map((u: UserProfile) => ({
        email: u.email,
        roles: u.user_roles
      })));
      setUsers(data.users);
    } catch (error: any) {
      console.error('❌ Error loading users:', error);
      toast({
        title: 'Erro ao carregar usuários',
        description: error.message || 'Não foi possível carregar os usuários. Verifique sua conexão.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    setChangingRoleUserId(userId);
    try {
      const targetUser = users.find((u) => u.id === userId);
      const currentRole = Array.isArray(targetUser?.user_roles)
        ? targetUser?.user_roles[0]?.role
        : (targetUser as any)?.user_roles?.role;

      if (currentRole === newRole) {
        toast({
          title: 'Nada a alterar',
          description: 'O usuário já possui este nível de acesso.',
        });
        return;
      }

      console.log('🔄 Changing role for user:', userId, 'from', currentRole, 'to', newRole);

      // Call backend function to change role securely
      const { data, error } = await supabase.functions.invoke('set-user-role', {
        body: { target_user_id: userId, new_role: newRole }
      });

      if (error) {
        console.error('Error changing role:', error);
        
        // Handle specific error cases
        if (error.message?.includes('Sessão expirada') || error.message?.includes('Não autorizado')) {
          toast({
            title: 'Sessão expirada',
            description: 'Faça login novamente para continuar.',
            variant: 'destructive',
          });
          return;
        }
        
        if (error.message?.includes('Apenas administradores')) {
          toast({
            title: 'Sem permissão',
            description: 'Apenas administradores podem alterar níveis de acesso.',
            variant: 'destructive',
          });
          return;
        }

        // Display backend error message if available
        toast({
          title: 'Erro',
          description: error.message || 'Erro ao alterar nível de acesso',
          variant: 'destructive',
        });
        return;
      }

      console.log('✅ Role changed successfully');
      toast({
        title: 'Sucesso',
        description: 'Nível de acesso atualizado',
      });

      // Add delay to ensure database has been updated
      await new Promise(resolve => setTimeout(resolve, 300));
      
      await fetchUsers();
      console.log('✅ Users refetched after role change');
    } catch (error: any) {
      console.error('Error changing role:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao alterar nível de acesso',
        variant: 'destructive',
      });
    } finally {
      setChangingRoleUserId(null);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Erro',
        description: 'As senhas não coincidem',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: 'Erro',
        description: 'A senha deve ter no mínimo 6 caracteres',
        variant: 'destructive',
      });
      return;
    }

    setProcessingAction(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('reset-user-password', {
        body: {
          user_id: selectedUser.id,
          new_password: newPassword
        }
      });

      if (error) throw error;

      toast({
        title: 'Senha redefinida!',
        description: `A senha de ${selectedUser.email} foi redefinida. O usuário pode fazer login com a nova senha.`,
      });

      setResetPasswordDialogOpen(false);
      setNewPassword('');
      setConfirmPassword('');
      setSelectedUser(null);
    } catch (error: any) {
      toast({
        title: 'Erro ao redefinir senha',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleToggleBlock = async (user: UserProfile, shouldBlock: boolean) => {
    setProcessingAction(true);
    try {
      const { data, error } = await supabase.functions.invoke('toggle-user-block', {
        body: {
          user_id: user.id,
          should_block: shouldBlock
        }
      });

      if (error) throw error;

      const action = shouldBlock ? 'bloqueado' : 'desbloqueado';
      toast({
        title: `Usuário ${action}!`,
        description: `${user.email} foi ${action} com sucesso.`,
      });

      fetchUsers();
    } catch (error: any) {
      toast({
        title: 'Erro ao alterar status',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setProcessingAction(false);
      setBlockAlertOpen(false);
      setSelectedUser(null);
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'default';
      case 'editor':
        return 'secondary';
      case 'cliente':
        return 'secondary';
      case 'guest':
        return 'outline';
      case 'rsvp':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield className="h-4 w-4" />;
      case 'editor':
        return <Edit className="h-4 w-4" />;
      case 'cliente':
        return <Edit className="h-4 w-4" />;
      case 'guest':
        return <Eye className="h-4 w-4" />;
      case 'rsvp':
        return <Calendar className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'editor':
        return 'Editor';
      case 'cliente':
        return 'Cliente';
      case 'guest':
        return 'Convidado';
      case 'rsvp':
        return 'RSVP';
      default:
        return role;
    }
  };

  const handleCreateUser = async () => {
    if (!newUserData.email || !newUserData.password || !newUserData.full_name) {
      toast({
        title: 'Erro',
        description: 'Email, senha e nome são obrigatórios',
        variant: 'destructive',
      });
      return;
    }

    // Company is now optional for all roles including cliente

    setCreating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(newUserData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erro ao criar usuário');
      }

      toast({
        title: 'Usuário criado com sucesso!',
        description: `O usuário ${newUserData.email} foi criado e pode fazer login com a senha fornecida.`,
      });

      setCreateDialogOpen(false);
      setNewUserData({
        email: '',
        password: '',
        full_name: '',
        company: '',
        role: 'guest'
      });
      fetchUsers();
    } catch (error: any) {
      toast({
        title: 'Erro ao criar usuário',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar onClearData={() => {}} />
        <main className="flex-1 overflow-auto p-4 md:p-8">
          <div className="max-w-6xl mx-auto space-y-6">
            <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Gerenciamento de Usuários</CardTitle>
                <CardDescription>
                  Gerencie os níveis de acesso, senhas e status dos usuários do sistema
                </CardDescription>
              </div>
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Criar Novo Usuário
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Criar Novo Usuário</DialogTitle>
                    <DialogDescription>
                      Preencha os dados do novo usuário. Uma senha temporária será definida.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="usuario@exemplo.com"
                        value={newUserData.email}
                        onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="full_name">Nome Completo</Label>
                      <Input
                        id="full_name"
                        placeholder="Nome do usuário"
                        value={newUserData.full_name}
                        onChange={(e) => setNewUserData({ ...newUserData, full_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Empresa (opcional)</Label>
                      <Input
                        id="company"
                        placeholder="Nome da empresa"
                        value={newUserData.company}
                        onChange={(e) => setNewUserData({ ...newUserData, company: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Senha Temporária</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Mínimo 6 caracteres"
                        value={newUserData.password}
                        onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">Nível de Acesso</Label>
                      <Select
                        value={newUserData.role}
                        onValueChange={(value: 'admin' | 'editor' | 'cliente' | 'guest' | 'rsvp') => 
                          setNewUserData({ ...newUserData, role: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Administrador</SelectItem>
                          <SelectItem value="editor">Editor</SelectItem>
                          <SelectItem value="cliente">Cliente</SelectItem>
                          <SelectItem value="guest">Convidado</SelectItem>
                          <SelectItem value="rsvp">RSVP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setCreateDialogOpen(false)}
                      disabled={creating}
                    >
                      Cancelar
                    </Button>
                    <Button onClick={handleCreateUser} disabled={creating}>
                      {creating ? 'Criando...' : 'Criar Usuário'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Perfil</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Nível de Acesso</TableHead>
                  <TableHead>Cadastrado em</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => {
                  const userRole = user.user_roles[0]?.role || 'guest';
                  
                  return (
                    <TableRow key={`${user.id}-${userRole}`}>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="rounded-full"
                          onClick={() => navigate(`/profile/${user.id}`)}
                          title="Ver perfil"
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar_url || undefined} alt={user.full_name || 'User'} />
                            <AvatarFallback>
                              {user.full_name?.charAt(0).toUpperCase() || <User className="h-4 w-4" />}
                            </AvatarFallback>
                          </Avatar>
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">{user.email}</TableCell>
                      <TableCell>{user.full_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={user.is_blocked ? 'destructive' : 'default'}>
                          {user.is_blocked ? 'Bloqueado' : 'Ativo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getRoleBadgeVariant(userRole)} className="gap-1">
                          {getRoleIcon(userRole)}
                          {getRoleLabel(userRole)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedUser(user);
                              setResetPasswordDialogOpen(true);
                            }}
                            title="Redefinir senha"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedUser(user);
                              if (!user.is_blocked) {
                                setBlockAlertOpen(true);
                              } else {
                                handleToggleBlock(user, false);
                              }
                            }}
                            title={user.is_blocked ? 'Desbloquear' : 'Bloquear'}
                            disabled={processingAction}
                          >
                            {user.is_blocked ? (
                              <Unlock className="h-4 w-4" />
                            ) : (
                              <Lock className="h-4 w-4" />
                            )}
                          </Button>
                          <Select
                            value={userRole}
                            onValueChange={(value) => handleRoleChange(user.id, value)}
                            disabled={changingRoleUserId === user.id}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Administrador</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                              <SelectItem value="cliente">Cliente</SelectItem>
                              <SelectItem value="guest">Convidado</SelectItem>
                              <SelectItem value="rsvp">RSVP</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Redefinir Senha</DialogTitle>
            <DialogDescription>
              Digite uma nova senha para {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova Senha</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar Senha</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Digite a senha novamente"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetPasswordDialogOpen(false);
                setNewPassword('');
                setConfirmPassword('');
                setSelectedUser(null);
              }}
              disabled={processingAction}
            >
              Cancelar
            </Button>
            <Button onClick={handleResetPassword} disabled={processingAction}>
              {processingAction ? 'Redefinindo...' : 'Redefinir Senha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block User Alert Dialog */}
      <AlertDialog open={blockAlertOpen} onOpenChange={setBlockAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Bloqueio</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja bloquear o acesso de <strong>{selectedUser?.email}</strong>?
              <br /><br />
              O usuário será desconectado imediatamente e não poderá fazer login até ser desbloqueado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedUser(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedUser && handleToggleBlock(selectedUser, true)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirmar Bloqueio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default UserManagement;